import { RouterName } from "src/routes/RouterName";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { IconArrowLeft, IconBriefcase, IconLock, IconMessages, IconRefresh } from "@tabler/icons";
import PageContainer from "src/components/container/PageContainer";
import PageHeader from "src/components/shared/PageHeader";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { dateTime } from "src/pages/cases/format";
import { PAGE_SIZE, findCustomer, getSessionMessages, getSessions } from "./actions";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";

// Ask Safrati answers are stored as HTML. Show them as plain text: nothing in
// a stored answer runs or loads in the CMS (DOMParser documents are inert).
function asText(html) {
  const text = String(html ?? "");
  if (!/<[a-z!/]/i.test(text)) return text.trim();
  const withBreaks = text.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n").replace(/<li[^>]*>/gi, "• ");
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  return (doc.body.textContent || "")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter((line, index, lines) => line || (index > 0 && lines[index - 1]))
    .join("\n")
    .trim();
}

function Message({ msg }) {
  const customer = msg.role === "user";
  return (
    <Stack alignItems={customer ? "flex-end" : "flex-start"}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, mb: 0.25 }}>
        {customer ? "Customer" : "Ask Safrati"} · {dateTime(msg.createdAt)}
      </Typography>
      <Box
        sx={{
          maxWidth: { xs: "90%", sm: "75%" },
          px: 1.5,
          py: 1,
          borderRadius: 3,
          ...(customer ? { borderBottomRightRadius: 6 } : { borderBottomLeftRadius: 6 }),
          bgcolor: customer ? "primary.main" : (t) => (t.palette.mode === "dark" ? "grey.800" : "#EAF0FA"),
          color: customer ? "primary.contrastText" : "text.primary",
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.9375rem", lineHeight: 1.5 }}>
          {asText(msg.content) || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

Message.propTypes = { msg: PropTypes.object.isRequired };

export default function UserAiChatSessions() {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [sessions, setSessions] = useState({ loading: true, failed: false, rows: [] });
  const [selectedId, setSelectedId] = useState(null);
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  const canSeeCustomers = Number(privileges["3"]?.access_read) === 1;
  const [customer, setCustomer] = useState({ loading: canSeeCustomers, data: null });

  useEffect(() => {
    if (!canSeeCustomers) return undefined;
    let live = true;
    findCustomer(userId, dispatch).then((data) => live && setCustomer({ loading: false, data }));
    return () => {
      live = false;
    };
  }, [userId, dispatch, canSeeCustomers]);
  const [thread, setThread] = useState({ loading: false, failed: false, page: 0, more: false, olderLoading: false, messages: [] });

  const loadSessions = useCallback(() => {
    setSessions((s) => ({ ...s, loading: true, failed: false }));
    getSessions(userId, dispatch)
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        rows.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setSessions({ loading: false, failed: false, rows });
      })
      .catch(() => setSessions((s) => ({ ...s, loading: false, failed: true })));
  }, [userId, dispatch]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const openSession = (id, page = 0) => {
    if (page === 0) {
      setSelectedId(id);
      setThread({ loading: true, failed: false, page: 0, more: false, olderLoading: false, messages: [] });
    } else {
      setThread((t) => ({ ...t, olderLoading: true }));
    }
    getSessionMessages(id, page, dispatch)
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        const totalPages = Number(response?.meta?.totalPages) || 0;
        setThread((t) => ({
          loading: false,
          failed: false,
          olderLoading: false,
          page,
          more: page + 1 < totalPages,
          messages: page === 0 ? rows : [...t.messages, ...rows.filter((r) => !t.messages.some((m) => m.id === r.id))],
        }));
      })
      .catch(() => setThread((t) => ({ ...t, loading: false, olderLoading: false, failed: page === 0 })));
  };

  const selected = sessions.rows.find((s) => s.id === selectedId);
  const showList = !phone || !selectedId;
  const showThread = !phone || !!selectedId;
  const latest = sessions.rows[0];
  const customerName = [customer.data?.first_name, customer.data?.last_name].filter(Boolean).join(" ").trim();

  return (
    <PageContainer title={customerName ? `Ask Safrati · ${customerName}` : "Ask Safrati conversations"} description="A customer's conversations with Ask Safrati">
      <ActionButton variant="text" startIcon={<IconArrowLeft size={16} />} onClick={() => navigate(RouterName.clients)} sx={{ mb: 1, ml: -1.5 }}>
        Back to Customers
      </ActionButton>
      <PageHeader
        title="Ask Safrati conversations"
        subtitle={
          customer.loading ? (
            <Skeleton width={260} />
          ) : (
            <>
              {customerName || "Customer"}
              {customer.data?.email ? ` · ${customer.data.email}` : ""}
              <Box component="span" sx={{ fontFamily: "monospace", ml: 1, color: "text.secondary" }}>
                #{userId}
              </Box>
              {canSeeCustomers && !customer.data ? " · no longer in the customer list" : ""}
            </>
          )
        }
        actions={
          customer.data?.email ? (
            <ActionButton startIcon={<IconBriefcase size={16} />} onClick={() => navigate(`/cases?q=${encodeURIComponent(customer.data.email)}`)}>
              Their applications
            </ActionButton>
          ) : null
        }
        sx={{ mb: 2 }}
      />

      <Alert severity="info" icon={<IconLock size={20} />} sx={{ mb: 2 }}>
        These are the customer’s private conversations with the assistant. Open one only to help this customer, and keep what you read inside
        Safrati.
      </Alert>

      <Card
        variant="outlined"
        sx={{
          display: "flex",
          p: 0,
          overflow: "hidden",
          // On a phone the list alone sizes to its content; an open conversation fills the screen.
          ...(phone && !selectedId ? {} : { height: { xs: "calc(100vh - 300px)", md: "calc(100vh - 320px)" }, minHeight: 420 }),
        }}
      >
        {showList && (
          <Stack sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0, borderRight: { md: 1 }, borderColor: { md: "divider" }, minHeight: 0 }}>
            <Typography variant="h6" component="h2" sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              Conversations{sessions.rows.length ? ` (${sessions.rows.length})` : ""}
            </Typography>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {sessions.loading && !sessions.rows.length && (
                <Box p={2} aria-busy="true" aria-label="Loading conversations">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
                  ))}
                </Box>
              )}
              {sessions.failed && (
                <Alert
                  severity="error"
                  sx={{ m: 2 }}
                  action={
                    <ActionButton onClick={loadSessions} startIcon={<IconRefresh size={16} />}>
                      Retry
                    </ActionButton>
                  }
                >
                  The conversations did not load.
                </Alert>
              )}
              {!sessions.loading && !sessions.failed && sessions.rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" p={2}>
                  No conversations for this customer. Ask Safrati no longer keeps conversations; older ones appear here.
                </Typography>
              )}
              <List disablePadding>
                {sessions.rows.map((session) => (
                  <ListItem key={session.id} disablePadding>
                    <ListItemButton
                      selected={session.id === selectedId}
                      onClick={() => openSession(session.id)}
                      sx={{ borderBottom: 1, borderColor: "divider", borderRadius: 0 }}
                    >
                      <ListItemText
                        primary={session.title?.trim() || "Untitled conversation"}
                        secondary={dateTime(session.updatedAt || session.createdAt)}
                        primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        )}

        {showThread && (
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            {!selectedId ? (
              <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, p: 4, textAlign: "center", color: "text.secondary" }}>
                <IconMessages size={32} />
                {latest ? (
                  <>
                    <Typography fontWeight={600} color="text.primary">
                      {sessions.rows.length === 1 ? "1 conversation" : `${sessions.rows.length} conversations`} with Ask Safrati
                    </Typography>
                    <Typography variant="body2">
                      The latest, “{latest.title?.trim() || "Untitled conversation"}”, was on {dateTime(latest.updatedAt || latest.createdAt)}.
                    </Typography>
                    <ActionButton onClick={() => openSession(latest.id)}>Open the latest</ActionButton>
                  </>
                ) : (
                  <>
                    <Typography fontWeight={600} color="text.primary">
                      {sessions.loading ? "Loading the conversations…" : "No conversations to show."}
                    </Typography>
                    {!sessions.loading && (
                      <Typography variant="body2">Ask Safrati no longer keeps conversations. Older ones appear here.</Typography>
                    )}
                  </>
                )}
              </Stack>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
                  {phone && (
                    <IconButton aria-label="Back to conversations" onClick={() => setSelectedId(null)} sx={iconButtonSx}>
                      <IconArrowLeft size={20} />
                    </IconButton>
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {selected?.title?.trim() || "Untitled conversation"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Started {dateTime(selected?.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  role="region"
                  aria-label={`Conversation: ${selected?.title?.trim() || "Untitled conversation"}`}
                  tabIndex={0}
                  sx={{ flex: 1, overflowY: "auto", px: { xs: 1.5, sm: 2.5 }, py: 2, bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "#F7FAFC") }}
                >
                  {thread.loading && (
                    <Stack alignItems="center" spacing={1} py={4} role="status">
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">
                        Loading the conversation…
                      </Typography>
                    </Stack>
                  )}
                  {thread.failed && (
                    <Alert
                      severity="error"
                      action={
                        <ActionButton onClick={() => openSession(selectedId)} startIcon={<IconRefresh size={16} />}>
                          Retry
                        </ActionButton>
                      }
                    >
                      The conversation did not load.
                    </Alert>
                  )}
                  {!thread.loading && !thread.failed && thread.messages.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      No messages in this conversation.
                    </Typography>
                  )}
                  <Stack spacing={1.5}>
                    {thread.messages.map((msg) => (
                      <Message key={msg.id} msg={msg} />
                    ))}
                  </Stack>
                  {thread.more && (
                    <Box textAlign="center" mt={2}>
                      <ActionButton variant="text" busy={thread.olderLoading} busyLabel="Loading…" onClick={() => openSession(selectedId, thread.page + 1)}>
                        Show {PAGE_SIZE} more messages
                      </ActionButton>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Stack>
        )}
      </Card>
    </PageContainer>
  );
}
